import { FullSingleLayerStack } from 'domains/viewer/ViewportsConfigurations';
import { AnalyticEventName, UserAnalytics } from 'modules/analytics/analytics';
import { Json } from 'types';
import { SupportedTextureTypes, SupportedTexturesMap } from 'utils/textureUtils';
import { CacheStrategy } from '../PixelCacheStategy';
import Deferred from 'utils/deferred';
import { GetStackSignedUrlsQuery, ModalityLutModule, VoiLutModule } from 'generated/graphql';
import { ImageLoadMetricSummary } from './LoadMetricsCollector';
import { ExtWindowName } from 'hooks/useExtensionState';

// Shared enums/constants for message types used between the app and the PixelData SharedWorker
export enum InboundMessageTypes {
  // Lifecycle and health-check
  Init = 'init',
  Ping = 'ping',
  TerminateConnection = 'terminate-connection',

  // Requests to load or fetch data
  LoadStack = 'load-stack',
  LoadFrame = 'load-frame',

  // Scheduling and cache/control operations
  UpdatePriority = 'update-priority',
  UpdateDeliveryMode = 'update-delivery-mode',
  CancelTransfer = 'cancel-transfer',
  ClearCache = 'clear-cache',
}

export enum OutboundMessageTypes {
  // Health-check and batching
  Pong = 'pong',
  BatchedMessages = 'batched-messages',

  // Transfer lifecycle (worker -> main thread)
  TransferStarting = 'transfer-starting',
  TransferComplete = 'transfer-complete',
  TransferCancelled = 'transfer-cancelled',
  TransferError = 'transfer-error',

  // High-level stack load lifecycle
  LoadComplete = 'load-complete',
  LoadError = 'load-error',

  // Per-frame data and processing
  FrameData = 'frame-data',
  DataReceived = 'data-received',
  HandleInitialFrame = 'handle-initial-frame',
  HandleFrame = 'handle-frame',

  // Analytics and telemetry
  AnalyticsTrack = 'analytics-track',
  AnalyticsReport = 'analytics-report',
  AnalyticsError = 'analytics-error',
}

export enum CancelTransferScope {
  All = 'all',
  Connection = 'connection',
  Stack = 'stack',
}

export enum PixelDataSharedWorkerError {
  OutOfMemory = 'Pixel Data OOM Transfer Error',
  NotEnoughMemory = 'Cannot fully load selected stacks. Reduce layout size and try again.',
  FrameNotFound = 'Frame not found',
  FailedToFetch = 'Failed to fetch pixel data from cache or DCMData',
}

export const cancelled = 'cancelled';

export type LoadCompleteMessage = {
  type: 'load-complete';
  stackSmid: string;
};
export type LoadErrorMessage = {
  type: 'load-error';
  stackSmid: string;
  error: string;
};
export type FrameMessageProto = {
  frameSmid: string;
  stackSmid: string;
  /**
   * VX-4775: absent when the stack is loading cache-only (`DataRequest.cacheOnly`). The frame still
   * reached the on-disk cache and the notification is still delivered — so the page's per-frame
   * bookkeeping (`_framesLoadedToCache`, `rangeMap`, `status`, `#pendingLoads`) stays intact — but
   * nothing is transferred into the renderer for a stack that would discard it on arrival.
   */
  pixels?: SupportedTextureTypes;
  range: [number, number];
  fromCache: boolean;
};
export type HandleFrameMessage = FrameMessageProto & {
  type: 'handle-frame';
};
export type HandleInitialFrameMessage = FrameMessageProto & {
  type: 'handle-initial-frame';
  volume?: SupportedTextureTypes;
  /** When true, volume allocation failed in the worker and the stack should fall back to per-frame loading */
  volumeAllocationFailed?: boolean;
};

/** Narrows a frame message to the initial-frame message (the only one carrying `volume`/`volumeAllocationFailed`). */
export function isInitialFrameMessage(
  message: HandleFrameMessage | HandleInitialFrameMessage | FrameData
): message is HandleInitialFrameMessage {
  return 'type' in message && message.type === 'handle-initial-frame';
}

/** A frame message that carries pixels — i.e. one the worker did not withhold for a cache-only stack. */
export type FrameMessageWithPixels = (
  | HandleFrameMessage
  | HandleInitialFrameMessage
  | FrameData
) & {
  pixels: SupportedTextureTypes;
};

/**
 * VX-4775: narrows a frame message to one carrying pixels. A cache-only stack's frames arrive without
 * them, so every path that inserts pixels into the volume or the per-frame maps goes through this.
 */
export function hasFramePixels(
  message: HandleFrameMessage | HandleInitialFrameMessage | FrameData
): message is FrameMessageWithPixels {
  return message.pixels != null;
}
export type HandleCacheOperationMessage = {
  framesProcessed: number;
  transferType: 'write' | 'read';
  estimatedCacheSize?: number;
};
export type AnalyticsErrorMessage = {
  type: 'analytics-error';
  error: Error;
  data?: Json;
};

export type AnalyticsReportMessage = {
  type: 'analytics-report';
  data: Partial<ImageLoadMetricSummary>;
};

export type AnalyticsTrackMessage = {
  type: 'analytics-track';
  name: AnalyticEventName;
  data?: object;
};

export type TransferCancelledMessage = {
  type: 'transfer-cancelled';
};

export type PongMessage = {
  type: 'pong';
  inits: number;
};

/**
 * VX-4284: why the worker withheld a pong on a wedged connection. `orphaned-port` = the port was
 * dropped from the LRU cache but still listening; `delivery-stall` = a connection that had
 * delivered at least once stopped delivering with work outstanding; `cold-start-stall` = a
 * connection with outstanding work that never delivered a single frame. For logging/telemetry only.
 */
export type PongRejectedReason = 'orphaned-port' | 'delivery-stall' | 'cold-start-stall';

/**
 * VX-4284: sent in place of a `pong` when the worker detects its own delivery wedge, so the page's
 * ping/pong watchdog (`useOpenPixelDataSharedWorker`) recreates the connection immediately rather
 * than waiting out two missed pongs (~80s).
 */
export type PongRejectedMessage = {
  type: 'pong-rejected';
  reason: PongRejectedReason;
};

export type WorkerSingleMessageData =
  | PongMessage
  | PongRejectedMessage
  | LoadCompleteMessage
  | LoadErrorMessage
  | HandleFrameMessage
  | HandleInitialFrameMessage
  | AnalyticsErrorMessage
  | AnalyticsReportMessage
  | AnalyticsTrackMessage
  | TransferCancelledMessage;

export type BatchedMessages = {
  type: 'batched-messages';
  messages: WorkerSingleMessageData[];
};

export type WorkerMessageData = WorkerSingleMessageData | BatchedMessages;

// #region Inbound Messages from the app to the worker
export type InitMessage = {
  type: 'init';
  SUPPORTED_TEXTURES: SupportedTexturesMap;
  cacheStrategy: CacheStrategy;
  /** VX-5499: download concurrency override; null/absent uses MAX_PARALLEL_REQUESTS */
  maxParallelRequests?: Nullable<number>;
  /** Window name of the caller */
  windowName: ExtWindowName | null;
  // user and session id are used to sync the worker's DD session
  // with the main page's session
  userAnalytics: Nullable<UserAnalytics>;
  datadogSessionId: Nullable<string>;
  /** Unique identifier for each page load to create separate Datadog views */
  sharedWorkerViewId: Nullable<string>;
  /** Optional configuration options */
  options?: {
    disableDecompression?: boolean;
    disablePostProcessing?: boolean;
    disableSignedUrls?: boolean;
  };
};

export type PingMessage = {
  type: 'ping';
};

export type LoadStackMessage = {
  type: 'load-stack';
  stack: FullSingleLayerStack;
  initialSlice: number;
  partOfInitialLoad: boolean;
  isDropped: boolean;
  priority: number;
  /**
   * VX-4775: load this stack into the cache without transferring its frames into the renderer. Set
   * for stacks the memory manager has put at `ImagingProviderLoadType.None`, which discard every
   * frame on arrival anyway. Promotion out of cache-only goes through `update-delivery-mode`.
   */
  cacheOnly: boolean;
  /** Window name of the caller */
  windowName: ExtWindowName | null;
};

export type LoadFrameMessage = {
  type: 'load-frame';
  stack: FullSingleLayerStack;
  frameIndex: number;
  priority: number;
  /**
   * Write the frame to the cache without transferring its pixels to the caller, for precache. Absent
   * means deliver, like {@link DataRequest.cacheOnly}.
   */
  cacheOnly?: boolean;
  /** Window name of the caller */
  windowName: ExtWindowName | null;
};

export type UpdatePriorityMessage = {
  type: 'update-priority';
  stackSmid: string;
  focus: number;
  isDropped: boolean;
  priority: number;
};

/**
 * VX-4775: flips an in-flight stack between cache-only and delivering, so a stack the memory manager
 * promotes out of `ImagingProviderLoadType.None` mid-load starts receiving pixels for its remaining
 * frames without cancelling and restarting the transfer. Kept separate from `update-priority` so a
 * routine scroll-driven reprioritization can't accidentally carry a stale delivery mode.
 */
export type UpdateDeliveryModeMessage = {
  type: 'update-delivery-mode';
  stackSmid: string;
  cacheOnly: boolean;
};

export type CancelTransferMessage =
  | {
      type: 'cancel-transfer';
      scope: CancelTransferScope.All;
    }
  | {
      type: 'cancel-transfer';
      scope: CancelTransferScope.Connection;
    }
  | {
      type: 'cancel-transfer';
      scope: CancelTransferScope.Stack;
      stack: FullSingleLayerStack;
    };

export type ClearCacheMessage = {
  type: 'clear-cache';
};

export type ResetMetricsMessage = {
  type: 'reset-metrics';
};

export type TerminateConnectionMessage = {
  type: 'terminate-connection';
};

export type TerminateMessage = {
  type: 'terminate';
};

export interface EnablePruningMessage {
  type: 'enable-pruning';
}

export type InboundMessageData =
  | InitMessage
  | PingMessage
  | LoadStackMessage
  | LoadFrameMessage
  | UpdatePriorityMessage
  | UpdateDeliveryModeMessage
  | CancelTransferMessage
  | ClearCacheMessage
  | ResetMetricsMessage
  | TerminateConnectionMessage
  | TerminateMessage
  | EnablePruningMessage;
// #endregion

export type TransferStartingMessage = {
  event: 'transfer-starting';
  requestId: string;
};

export type TransferErrorOutgoingMessage = {
  event: 'transfer-error';
  stacks: string[];
  error: string;
};

export type TransferCompleteMessage = {
  event: 'transfer-complete';
  requestId: string;
  framesTransferred: number;
  endingPriority: number;
  endingFocus: number;
};

export type FrameDataMessage = {
  event: 'frame-data';
  cachePixelUrl: string;
  dcmdataPixelUrl: Nullable<string>;
};

export type OutgoingMessage =
  | TransferStartingMessage
  | TransferErrorOutgoingMessage
  | TransferCompleteMessage
  | FrameDataMessage;

export type DataReceivedMessage = {
  event: 'data-received';
  frameSmid: string;
  pixels: SupportedTextureTypes;
};

export type CallbackMessage = OutgoingMessage | DataReceivedMessage;

export type MessageReceivedCallback = (message: CallbackMessage) => void;

export type DataRequest = {
  stack: FullSingleLayerStack;
  focus: number;
  partOfInitialLoad: boolean;
  isDropped: boolean;
  processing: boolean;
  priority: number;
  /**
   * VX-4775: withhold pixels from the page for this stack; see {@link LoadStackMessage.cacheOnly}.
   * Optional, and absent means deliver — the safe default, since a request that wrongly withholds
   * renders blank while one that wrongly delivers only wastes an allocation.
   */
  cacheOnly?: boolean;
  messageQueue: Array<WorkerMessageData>;
  windowName: ExtWindowName | null;
};

export type DedupedDataRequest = DataRequest & {
  connectionId: string;
};

export type HandleMessage = (event: MessageEvent) => void;

export const REMAINING_FRAMES_TIMEOUT = 250;
export const MAX_PAYLOAD = 500 * 1024 * 1024; // 500 MB
/** Maximum volume allocation size in bytes. Volumes exceeding this fall back to per-frame loading. */
export const MAX_VOLUME_ALLOCATION_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

/**
 * Total byte size of a contiguous volume allocation, gated against {@link MAX_VOLUME_ALLOCATION_BYTES}.
 * Shared by the worker (PixelWorkerConnection) and the main thread (ProgressiveImagingProvider) so the
 * two budget checks can't drift. `colorChannels` is here to future-proof the formula for color data;
 * callers that don't account for it pass the default of 1.
 */
export function computeVolumeBytes(
  bytesPerElement: number,
  pixelsPerFrame: number,
  frameCount: number,
  colorChannels = 1
): number {
  return bytesPerElement * pixelsPerFrame * frameCount * colorChannels;
}

/**
 * Events emitted from the PixelDataLoader to the PixelWorkerConnection.
 *
 * The loader rail always carries pixels — whether a frame came off the network or out of the cache,
 * the loader has the bytes in hand. Withholding them is a property of the page-facing rail only
 * (VX-4775), which is why `FrameData.pixels` is required while `FrameMessageProto.pixels` is not.
 */
export type LoaderCallbackArg =
  | {
      stackSmid: string;
      type: 'initial-frame' | 'frame' | 'complete';
      data: FrameData | null;
    }
  | {
      stackSmid: string;
      type: 'cache-transfer-complete';
      data: HandleCacheOperationMessage;
    }
  | {
      stackSmid: string;
      type: 'error';
      data: string;
    };

export type LoaderCallback = (arg: LoaderCallbackArg) => void;

export type CacheObject = {
  stackSmid: string;
  frameSmid: string;
  data: {
    pixels: SupportedTextureTypes;
  };
};

export type PixelMapEntryState = 'received' | 'written';
export type PixelMapEntry = {
  frameInfo: OrderedFrame;
  data: {
    pixels: SupportedTextureTypes;
  };
  status: PixelMapEntryState;
  size: number;
  writeId?: string;
};

export type PixelMap = Map<string, PixelMapEntry>;

export type FrameResponse = ArrayBuffer | null | typeof cancelled;

export type QueuedFrame = {
  stackSmid: string;
  url: string;
  includeCredentials: boolean;
  frameSmid: string;
  sortIndex: number;
  deferred: Deferred<FrameResponse>;
  priority: number;
  expirationTime?: number;
};

export type DedupedQueuedFrame = QueuedFrame & {
  pixelUrl: GetStackSignedUrlsQuery['pixelURLs'][number];
  abortSignal?: AbortSignal;
};

export type LoadRequest = {
  orderedFrames: ReadonlyArray<OrderedFrame>;
  priority: number;
  focus: number;
  stackSmid: string;
};

export type CancelRequest =
  | {
      scope: CancelTransferScope.All;
    }
  | {
      scope: CancelTransferScope.Connection;
    }
  | {
      scope: CancelTransferScope.Stack;
      stackSmid: string;
    };

export type DedupedCancelRequest = CancelRequest & {
  connectionId: string;
};

export type OrderedFrame = {
  seriesSmid: string;
  frameSmid: string;
  stackSmid: string;
  isMultiFrame: boolean;
  sortIndex: number;
  modules: {
    modalityLut?: ModalityLutModule | null;
    voiLut?: VoiLutModule | null;
  };
};

// map of stack smid to sets of failed frame smids
export type DownloadStacksResult = Map<string, Set<string>>;

/** Map of stackSmids to window names. Used for metrics attribution */
export type StackWindowNameMap = Map<string, Set<string | null>>;

/** internal state of Http Request Queue for a given frame smid */
export type FrameRequest = {
  stackSmid: string;
  priority: number;
  pixelUrl: GetStackSignedUrlsQuery['pixelURLs'][number];
  connections: Array<{ connectionId: string; deferred: Deferred<FrameResponse>; priority: number }>;
  abortController: AbortController;
};

/**
 * Structural shape of a PixelWorkerConnection that workerStatus() needs. Declared as a structural
 * interface (rather than importing the class) so DedupedPixelWorkerConnection, which narrows its
 * imageLoader type via @ts-expect-error and therefore isn't a nominal subtype, still satisfies the
 * constraint.
 */
export interface WorkerConnectionSnapshotSource {
  port: MessagePort;
  windowName: string | null;
  /**
   * VX-4864: lets workerStatus() join a connection to its RequestQueue delivery stats, which are
   * keyed by connectionId. Declared on the base PixelWorkerConnection as a lazily-minted nanoid, so
   * both the legacy and deduped flavors satisfy this.
   */
  connectionId: string;
  activeRequests: { size: number };
  _singleFrameQueue: { messageQueue: Array<unknown> };
  stackExpectedFramesSize: number;
}

export type OriginCacheTypes = 'edge-pixel-cache' | 'pixel-cache' | 'no-cache';
export type OriginService = 'dcmdata' | 'cloudfront';
export type MixedOriginCacheType = 'mixed';

export type FrameData = {
  frameSmid: string;
  pixels: SupportedTextureTypes;
  range: [number, number];
  fromCache: boolean;
  volume?: SupportedTextureTypes;
};

export type LoaderOptions = {
  disableDecompression?: boolean;
  disablePostProcessing?: boolean;
  disableSignedUrls?: boolean;
};

export interface ViewerDownloadingFlags {
  cacheStrategy: CacheStrategy;
  maxParallelRequests: Nullable<number>;
}
